import React from "react";

/**
 * DeleteModal - Silme onayı için paylaşılan modal.
 * Zuzzuu_2.0/shared/components/Modals/DeleteModal.jsx'den birebir kopya —
 * tüm app'lerde tutarlı görünüm için inline style ile yazılmış, sıfır bağımlılıklı.
 *
 * Props: open, onClose, onConfirm, title, description, itemName (opsiyonel)
 */

const AlertTriangleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 5L4.64564 15.9758C4.77037 18.0962 4.83273 19.1564 5.28528 19.961C5.68366 20.6692 6.28832 21.2394 7.01876 21.5955C7.84851 22 8.91054 22 11.0346 22H12.9654C15.0895 22 16.1515 22 16.9812 21.5955C17.7117 21.2394 18.3163 20.6692 18.7147 19.961C19.1673 19.1564 19.2296 18.0962 19.3544 15.9758L20 5M4 5H2M4 5H20M20 5H22M16 5L15.547 3.64105C15.3503 3.05094 15.252 2.75588 15.0695 2.53774C14.9084 2.3451 14.7016 2.19599 14.4679 2.10408C14.2032 2 13.8922 2 13.2702 2H10.7298C10.1078 2 9.79677 2 9.53213 2.10408C9.29844 2.19599 9.09156 2.3451 8.93047 2.53774C8.74804 2.75588 8.64969 3.05094 8.45298 3.64105L8 5M10 10V17M14 10V14" />
  </svg>
);

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 1300,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0,0,0,0.5)",
  padding: 20,
};

const contentStyle = {
  position: "relative",
  width: "100%",
  maxWidth: 352,
  borderRadius: 20,
  background: "#F4F4F4",
  boxShadow: "0px 10px 15px 0px rgba(0,0,0,0.08)",
  padding: "80px 44px 27px",
  boxSizing: "border-box",
  textAlign: "center",
};

const iconWrapStyle = {
  position: "absolute",
  left: "50%",
  top: -64,
  transform: "translateX(-50%)",
  width: 100,
  height: 100,
  borderRadius: "50%",
  background: "#FD5995",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const titleStyle = {
  margin: 0,
  marginBottom: 16,
  fontSize: 22,
  fontWeight: 600,
  color: "#FD5995",
  fontFamily: "var(--font-family, inherit)",
};

const descStyle = {
  margin: 0,
  marginBottom: 24,
  fontSize: 18,
  lineHeight: 1.4,
  color: "#000",
  fontFamily: "var(--font-family, inherit)",
};

const buttonContainerStyle = {
  display: "flex",
  gap: 8,
  justifyContent: "center",
};

const deleteButtonStyle = {
  padding: "10px 16px",
  borderRadius: 6,
  border: "1px solid #FD5995",
  background: "#FFF",
  color: "#FD5995",
  fontFamily: "var(--font-family, inherit)",
  fontSize: 16,
  fontWeight: 500,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  textTransform: "capitalize",
};

const cancelButtonStyle = {
  padding: "10px 16px",
  borderRadius: 6,
  border: "1px solid #D8D8D8",
  background: "#FFF",
  color: "#000",
  fontFamily: "var(--font-family, inherit)",
  fontSize: 16,
  fontWeight: 500,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  textTransform: "capitalize",
};

const RefreshIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
  </svg>
);

export default function DeleteModal({
  open = false,
  onClose,
  onConfirm,
  title = "Silmek istediğinize emin misiniz?",
  description = "Bu işlem geri alınamaz.",
  itemName = null,
  deleteLabel = "Sil",
  cancelLabel = "İptal",
  // Renk / ikon override — delete dışı onay diyalogları için
  accentColor = "#FD5995",   // ikon arka planı, başlık ve buton rengi
  confirmIcon = "delete",    // "delete" | "refresh"
}) {
  if (!open) return null;

  const finalDescription = description;

  const handleConfirm = () => {
    onConfirm?.();
    onClose?.();
  };

  const ConfirmIcon = confirmIcon === "refresh" ? RefreshIcon : DeleteIcon;

  return (
    <div
      role="presentation"
      style={overlayStyle}
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div style={contentStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ ...iconWrapStyle, background: accentColor }}>
          <AlertTriangleIcon />
        </div>
        <h2 style={{ ...titleStyle, color: accentColor }}>{title}</h2>
        <p style={descStyle}>{finalDescription}</p>
        <div style={buttonContainerStyle}>
          <button
            type="button"
            style={{ ...deleteButtonStyle, border: `1px solid ${accentColor}`, color: accentColor }}
            onClick={handleConfirm}
          >
            <ConfirmIcon />
            {deleteLabel}
          </button>
          <button type="button" style={cancelButtonStyle} onClick={onClose}>
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
